'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { Image } from '@/components/client';

import { ONLINE_EVENTS, WELLPOINT_INFO } from '../constants';
import { EventCard } from './EventCard';

const EASE = {
  smooth: [0.6, 0.01, 0.05, 0.95] as const,
  spring: [0.34, 1.56, 0.64, 1] as const,
  elegant: [0.16, 1, 0.3, 1] as const,
};

const CONTAINER_ANIMATION = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const HEADER_ANIMATION = {
  hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.8,
      ease: EASE.elegant,
    },
  },
};

const CARD_CONTAINER_ANIMATION = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const BOX_ANIMATION = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: EASE.smooth,
    },
  },
};

const LOGO_ANIMATION = {
  hidden: { opacity: 0, scale: 0.8, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.08,
      ease: EASE.spring,
    },
  }),
};

export const OnlineEventSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="relative w-full overflow-hidden bg-B-500 px-32 py-80">
      <motion.div
        ref={ref}
        variants={CONTAINER_ANIMATION}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="flex flex-col gap-30"
      >
        <motion.header
          variants={HEADER_ANIMATION}
          className="flex flex-col gap-12 text-Gray-white"
        >
          <h2 className="w-full font-800 typo-h4">참가 신청 EVENT</h2>
          <p className="w-full font-600 typo-body3">
            참가 신청 후 행사장에 방문한 분들 중<br />
            103명을 추첨해 푸짐한 선물을 드려요.
          </p>
        </motion.header>

        <motion.ul
          variants={CARD_CONTAINER_ANIMATION}
          className="flex flex-col items-center gap-60 px-28"
        >
          {ONLINE_EVENTS.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </motion.ul>

        <motion.div
          variants={BOX_ANIMATION}
          whileHover={{
            scale: 1.01,
            y: -2,
            transition: { duration: 0.3, ease: EASE.smooth },
          }}
          className="group relative flex w-full flex-col items-center gap-20 rounded-xlarge border border-Gray-white/20 bg-Gray-white/10 px-32 py-40 backdrop-blur-md"
        >
          <div className="absolute inset-0 rounded-xlarge bg-gradient-to-br from-Gray-white/0 via-Gray-white/5 to-Gray-white/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          <div className="relative z-10 flex flex-col items-center gap-12">
            <h3 className="text-center font-800 typo-h5">
              <span className="text-Gray-black">웰포인트</span>
              <span className="text-Gray-white">가 뭔가요?</span>
            </h3>
            <p className="text-center font-500 text-Gray-white typo-body3">
              웰로에서 드리는{' '}
              <span className="font-500 text-Gray-black">현금성 포인트</span>
              에요.
              <br />
              네이버페이, 카카오페이, 배달의민족 등
              <br />
              다양한 쿠폰으로 교환이 가능합니다.
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-10">
            {WELLPOINT_INFO.logos.map((logo, index) => (
              <motion.div
                key={index}
                custom={index}
                variants={LOGO_ANIMATION}
                whileHover={{
                  scale: 1.1,
                  y: -4,
                  transition: { duration: 0.2, ease: EASE.spring },
                }}
                whileTap={{ scale: 0.95 }}
                className="bg-white size-55 shrink-0 overflow-hidden rounded-medium shadow-md transition-shadow duration-300 hover:shadow-lg"
              >
                <Image
                  alt={`쿠폰 교환처 ${index + 1}`}
                  className="size-full object-contain"
                  width={55 * 2}
                  height={55 * 2}
                  src={logo}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
